// adsService.ts
/*
//広告情報を扱うサービス
// 取得した情報はIndexedDBにキャッシュし、オフラインにも対応します。
*/

import useSWR, { mutate } from 'swr';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { appId, db } from '../firebase';
import { Ad } from '../types/index';

// NGワードリスト
const bannedWords = [
  'アダルト', 'エロ', 'ポルノ', 'セックス', '性交', 'SEX',
  '殺人', '自殺', '暴力', 'テロ', '麻薬', 'ドラッグ',
  '差別', 'ヘイト', '誹謗中傷', '脅迫', '詐欺', 'スパム'
];


/**
 * 広告バリデーション
 * @param ad - 広告データ
 * @returns エラーメッセージ（問題なければnull）
 */
export const validateAd = (ad: Partial<Ad>): string | null => {
  const requiredFields: (keyof Ad)[] = ['title', 'category', 'authorNickname', 'url', 'age_from', 'age_to'];
  for (const field of requiredFields) {
    const value = ad[field];
    if (field === 'age_from' || field === 'age_to') {
      if (value === undefined || value === null || value === '') return `「${field}」は必須項目です。`;
    } else {
      if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) return `「${field}」は必須項目です。`;
    }
  }
  const textFields = [ad.title, ad.category, ad.authorNickname, ad.description || ''].join(' ').toLowerCase();
  if (bannedWords.some(word => textFields.includes(word.toLowerCase()))) return '不適切な単語が含まれています。';
  const ageFrom = Number(ad.age_from);
  const ageTo = Number(ad.age_to);
  if (isNaN(ageFrom) || isNaN(ageTo) || ageFrom < 1 || ageTo < 1) return '年齢は1以上を指定してください。';
  if (ageFrom > ageTo) return '対象年齢の範囲が正しくありません（最小年齢 ≤ 最大年齢）。';
  if (ad.url) { try { new URL(ad.url); } catch { return 'URLの形式が正しくありません。'; } }
  if (ad.image_url) { try { new URL(ad.image_url); } catch { return '画像URLの形式が正しくありません。'; } }
  return null;
};

/**
 * Firestoreから全ての広告を取得する関数
 * @returns 広告データの配列
 */
export const fetchAllAds = async (): Promise<Ad[]> => {
  const adsRef = collection(db, `artifacts/${appId}/public/data/ads`);
  const snapshot = await getDocs(adsRef);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Ad));
};

/**
 * 新しい広告をFirestoreに追加する関数
 * @param newAdData - 追加する広告データ
 * @returns 追加された広告データ
 */
export const addAd = async (newAdData: Omit<Ad, 'id'>): Promise<Ad> => {
  const adsRef = collection(db, `artifacts/${appId}/public/data/ads`);
  const dataToSave = { ...newAdData, age_from: typeof newAdData.age_from === 'string' ? parseInt(newAdData.age_from) || 0 : newAdData.age_from, age_to: typeof newAdData.age_to === 'string' ? parseInt(newAdData.age_to) || 0 : newAdData.age_to, };
  const docRef = await addDoc(adsRef, dataToSave);
  const addedAd = { id: docRef.id, ...dataToSave } as Ad;
  mutate(['ads-local', appId]);
  return addedAd;
};

/**
 * 既存の広告をFirestoreで更新する関数
 * @param adId - 更新する広告のID
 * @param updatedData - 更新データ
 */
export const updateExistingAd = async (adId: string, updatedData: Partial<Ad>): Promise<void> => {
  const adDocRef = doc(db, `artifacts/${appId}/public/data/ads`, adId);
  const dataToUpdate: Partial<Omit<Ad, 'id'>> = { ...updatedData };
  if (typeof updatedData.age_from === 'string') dataToUpdate.age_from = parseInt(updatedData.age_from) || 0;
  if (typeof updatedData.age_to === 'string') dataToUpdate.age_to = parseInt(updatedData.age_to) || 0;
  await updateDoc(adDocRef, dataToUpdate);
  mutate(['ads-local', appId]);
};

/**
 * 広告をFirestoreから削除する関数
 * @param adId - 削除する広告のID
 */
export const deleteAd = async (adId: string): Promise<void> => {
  const adDocRef = doc(db, `artifacts/${appId}/public/data/ads`, adId);
  await deleteDoc(adDocRef);
  mutate(['ads-local', appId]);
};



